import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiLoader, FiCheckCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { extractGolfNowFacilityId } from '../services/golfNowService';
import { submitFacilityOverride } from '../services/overridesService';

/**
 * Paste-a-GolfNow-link modal. Extracts the facility id from the pasted URL and
 * saves it as a per-user override for the club, then hands the new id back so
 * the results page can re-run. Never writes the shared golf_clubs row.
 *
 * Props:
 *   isOpen        - visibility
 *   onClose       - backdrop / Cancel / X
 *   golfClubId    - golf_clubs.id (UUID) to attach the override to
 *   courseName    - display name for copy
 *   mode          - 'expecting' (no live tee times) | 'incorrect' (wrong ones)
 *   onSubmitted   - (facilityId) => void, called after a successful save
 */
export default function GolfNowLinkModal({
  isOpen,
  onClose,
  golfClubId,
  courseName,
  mode = 'expecting',
  onSubmitted,
}) {
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const extractedId = useMemo(() => extractGolfNowFacilityId(url), [url]);
  const showInvalid = url.trim().length > 0 && !extractedId;

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!extractedId || !golfClubId) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitFacilityOverride(golfClubId, extractedId, url.trim());
      onSubmitted?.(extractedId);
    } catch (err) {
      setError(err.message || 'Could not save your tee-time link');
      setSubmitting(false);
    }
  };

  const heading = mode === 'incorrect' ? 'Wrong tee times?' : 'Expecting live tee times?';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]"
        onClick={submitting ? undefined : onClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-card rounded-t-lg sm:rounded-lg shadow-modal max-w-md w-full mb-20 sm:mb-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <h2 className="text-lg font-bold text-text-primary">{heading}</h2>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                aria-label="Close"
                className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center hover:bg-border-subtle transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
              </button>
            </div>

            <div className="text-sm text-text-secondary mb-4 space-y-2">
              <p>
                {mode === 'incorrect'
                  ? `If these tee times aren't for ${courseName || 'this course'}, find it on GolfNow and paste the page link below. We'll use it for your results.`
                  : `Find ${courseName || 'this course'} on GolfNow and paste the page link below. We'll pull its live tee times into your results.`}
              </p>
              <p className="text-text-muted">
                We currently only integrate with GolfNow. Want another provider?{' '}
                <Link to="/support" className="text-accent hover:text-interactive-hover font-medium">
                  Request it via Help and support
                </Link>
                .
              </p>
            </div>

            <label className="block text-xs font-semibold text-text-secondary mb-1.5">
              GolfNow page link
            </label>
            <input
              type="url"
              inputMode="url"
              autoComplete="off"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setError(null); }}
              placeholder="https://www.golfnow.com/tee-times/facility/..."
              disabled={submitting}
              className="w-full px-3 py-2.5 rounded-lg border border-border-subtle bg-surface text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
            />

            {extractedId && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-status-success">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                Found facility id {extractedId}
              </p>
            )}
            {showInvalid && (
              <p className="mt-2 text-sm text-text-muted">
                That doesn't look like a GolfNow facility link yet. It should contain
                {' '}<span className="font-mono">/facility/&lt;number&gt;</span>.
              </p>
            )}
            {error && (
              <p className="mt-2 text-sm text-status-error">{error}</p>
            )}

            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 gap-2 sm:gap-0">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2.5 rounded-lg border border-border-subtle text-text-secondary hover:bg-surface transition-colors text-sm font-semibold disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !extractedId}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 bg-accent text-white hover:bg-interactive-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting && <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />}
                Use these tee times
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
